import { useCallback, useEffect, useState } from "react";
import { Download, FileStack, Loader2, RefreshCw } from "lucide-react";
import { AdminEligibility } from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export function DocumentPackPanel({ check }) {
  const paid = check.payment_status === "paid";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await AdminEligibility.docPackVersions(check.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not load document packs");
    }
  }, [check.id]);

  useEffect(() => { load(); }, [load]);

  const jobActive = ["queued", "running"].includes(data?.latest_job?.status);
  useEffect(() => {
    if (!jobActive) return undefined;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [jobActive, load]);

  const generate = async () => {
    setBusy(true);
    try {
      await AdminEligibility.docPackGenerate(check.id);
      toast.success("Document Pack generation started");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || "Could not start Document Pack");
    } finally {
      setBusy(false);
    }
  };

  const download = async (v) => {
    setLinking(v.id);
    try {
      const { url } = await AdminEligibility.docPackLink(check.id, v.id);
      window.open(`${process.env.REACT_APP_BACKEND_URL}${url}`, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not issue download link");
    } finally {
      setLinking(null);
    }
  };

  const versions = data?.versions || [];
  return (
    <div data-testid="doc-pack-panel" className="mx-3 mb-3 rounded-xl border border-[#D4AF37]/25 bg-white/[0.03] p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] flex items-center gap-1.5">
          <FileStack size={11} /> Document Pack v2 · versioned · watermarked
        </div>
        <button
          data-testid="doc-pack-generate-btn"
          disabled={!paid || busy || jobActive}
          onClick={generate}
          title={paid ? "Compile cover + manifest + all KYC documents" : "Enabled only after payment proof is verified"}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37] text-[#0A1F44] text-[11px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition"
        >
          {busy || jobActive ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          {jobActive ? "Generating…" : "GENERATE DOCUMENT PACK"}
        </button>
      </div>
      {!paid && (
        <div data-testid="doc-pack-locked-note" className="mt-2 text-[11px] text-amber-200/80">
          Locked — payment proof must be verified before a pack can be generated.
        </div>
      )}
      {data?.latest_job?.status === "failed" && (
        <div data-testid="doc-pack-job-failed" className="mt-2 text-[11px] text-rose-300">
          Last generation failed: {data.latest_job.error}
        </div>
      )}
      <div className="mt-2 space-y-1.5">
        {versions.length === 0 && (
          <div data-testid="doc-pack-empty" className="text-[11px] text-white/45">No pack generated yet.</div>
        )}
        {versions.map((v) => (
          <div key={v.id} data-testid={`doc-pack-version-${v.version_no}`} className="flex items-center gap-2 text-[11px] text-white/75">
            <span className="px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#F0D788] font-mono">v{v.version_no}</span>
            <div className="flex-1 min-w-0">
              <div className="text-white/90">{v.pages} pages · {v.manifest?.length ?? 0} docs · {(v.pdf_bytes / 1024).toFixed(0)} KB</div>
              <div className="text-white/40 truncate">{fmt(v.generated_at)} · {v.generated_by} · sha {v.pdf_sha256?.slice(0, 12)}</div>
            </div>
            <button
              data-testid={`doc-pack-download-${v.version_no}`}
              disabled={linking === v.id}
              onClick={() => download(v)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[#D4AF37]/40 hover:border-[#D4AF37]/80 text-[10px] text-[#F0D788] disabled:opacity-50"
            >
              {linking === v.id ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />} {data?.link_ttl_minutes}-min link
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
